import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { signOut } from '../../services/authService';
import { 
  LayoutDashboard, 
  Calendar, 
  Package, 
  Users, 
  CreditCard, 
  BarChart3, 
  Building2, 
  ShoppingBag,
  Store,
  ClipboardList,
  Send,
  Utensils,
  Settings,
  LogOut,
  Globe,
  ChevronLeft,
  ChevronRight,
  Download,
  ScanLine,
  WalletCards,
  ShieldCheck
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { canAccessMenuItem } from '../../utils/collaboratorPermissions';
import UserNotifications from './UserNotifications';
import './Navigation.css';

const Navigation = ({ isCollapsed = false, onToggle }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [installPrompt, setInstallPrompt] = useState(null);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setInstallPrompt(event);
    };

    const handleInstalled = () => setInstallPrompt(null);

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const handleInstallApp = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  };

  const navItems = [
    { path: '/', icon: LayoutDashboard, label: t('nav.dashboard'), roles: ['admin', 'operational'] },
    { path: '/marketplace', icon: ShoppingBag, label: t('nav.marketplace'), roles: ['operational'] },
    { path: '/agendamento', icon: Send, label: 'Agendamento', roles: ['admin', 'operational', 'collaborator'], permission: 'scheduler' },
    { path: '/validar-voucher', icon: ScanLine, label: 'Validar voucher', roles: ['admin', 'collaborator'], permission: 'voucher_validation' },
    { path: '/agenda', icon: Calendar, label: t('nav.agenda'), roles: ['admin', 'operational', 'collaborator'], permission: 'reservations' },
    { path: '/reservations', icon: Calendar, label: t('nav.reservations'), roles: ['admin', 'operational', 'collaborator'], permission: 'reservations' },
    { path: '/meu-financeiro', icon: WalletCards, label: 'Meu financeiro', roles: ['collaborator'], permission: 'scheduler' },
    { path: '/services', icon: Package, label: t('nav.services'), roles: ['admin'] },
    { path: '/clients', icon: Users, label: t('nav.clients'), roles: ['admin', 'operational'] },
    { path: '/collaborators', icon: Users, label: t('nav.collaborators'), roles: ['admin', 'operational'] },
    { path: '/payments', icon: CreditCard, label: t('nav.payments'), roles: ['admin', 'operational'] },
    { path: '/establishments', icon: Utensils, label: t('nav.establishments'), roles: ['admin', 'operational'] },
    { path: '/orders', icon: ClipboardList, label: t('nav.orders'), roles: ['admin', 'operational', 'collaborator'], permission: 'orders' },
    { path: '/pos', icon: Store, label: t('nav.pos'), roles: ['admin', 'operational', 'collaborator'], permission: 'pos' },
    { path: '/reports', icon: BarChart3, label: t('nav.reports'), roles: ['admin'] },
    { path: '/audit', icon: ShieldCheck, label: 'Auditoria', roles: ['admin'] },
  ];
  const rootItems = [
    { path: '/?rootTab=panel', icon: LayoutDashboard, label: 'Painel Root', roles: ['root'] },
    { path: '/?rootTab=billing', icon: WalletCards, label: 'Cobranças e comunicações', roles: ['root'] },
    { path: '/?rootTab=companies', icon: Building2, label: 'Empresas e faturamento', roles: ['root'] },
    { path: '/?rootTab=logs', icon: ShieldCheck, label: 'Logs do sistema', roles: ['root'] }
  ];
  const settingsItem = { path: '/settings', icon: Settings, label: t('nav.settings'), roles: ['root', 'admin', 'operational'] };

  const filteredNavItems = [...navItems, ...rootItems].filter(item => canAccessMenuItem(userData, item));
  const canAccessSettings = canAccessMenuItem(userData, settingsItem);

  return (
    <nav className={`navigation ${isCollapsed ? 'navigation--collapsed' : ''}`}>
      <UserNotifications isCollapsed={isCollapsed} />
      <div className="nav-header">
        <button className="nav-toggle" onClick={onToggle} title={isCollapsed ? 'Expandir menu' : 'Minimizar menu'}>
          {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
        <img src="/logo.png" alt="SDC Turismo" className="nav-logo" />
        <h2>{t('app.name')}</h2>
        <span className="user-role">{t(`roles.${userData?.role}`)}</span>
      </div>

      <ul className="nav-list">
        {filteredNavItems.map((item) => (
          <li key={`${item.path}-${item.label}`}>
            <Link to={item.path} className="nav-item">
              <item.icon size={20} />
              <span>{item.label}</span>
            </Link>
          </li>
        ))}

        <li className="mobile-logout-item">
          <button type="button" onClick={handleLogout} className="nav-item logout-btn">
            <LogOut size={20} />
            <span>{t('auth.logout')}</span>
          </button>
        </li>
      </ul>

      <div className="nav-footer">
        <div className="language-selector">
          <Globe size={16} />
          <select 
            value={i18n.language} 
            onChange={(e) => changeLanguage(e.target.value)}
            className="language-select"
          >
            <option value="pt-BR">Português</option>
            <option value="en">English</option>
            <option value="es">Español</option>
          </select>
        </div>

        {canAccessSettings && (
          <Link to={settingsItem.path} className="nav-item">
            <settingsItem.icon size={20} />
            <span>{settingsItem.label}</span>
          </Link>
        )}

        {installPrompt && (
          <button onClick={handleInstallApp} className="nav-item" type="button">
            <Download size={20} />
            <span>Instalar app</span>
          </button>
        )}

        <button onClick={handleLogout} className="nav-item logout-btn">
          <LogOut size={20} />
          <span>{t('auth.logout')}</span>
        </button>
      </div>
    </nav>
  );
};

export default Navigation;
